import { receiptBenefits } from '@pxtra-client-lib-legacy';
import {
  Base64FileField,
  MonthFieldRequest,
  OperationEnum,
  MonthField,
  EmployeeBenefitBudget,
  EmployeeExtended,
  EmployeeListItem,
  EmployeeBenefitSettings
} from '@pxtra-client-lib-legacy/gen';
import i18next, { TFunction } from 'i18next';
import CompanyStore from '../stores/companyStore';
import { Writeable } from './writeable';
import * as Mime from 'mime';
import { FileWithPreview } from '../lib/components';
import { de, enUS } from 'date-fns/locale';
import i18n from './i18n';
import { format } from 'date-fns';
import { t } from 'i18next';
import { ValidationErrorsBIC, ValidationErrorsIBAN } from 'ibantools';
import { FormikValues } from 'formik';
import { runInAction } from 'mobx';
import { Benefit } from '@pxtra-client-lib-legacy/types';
import { Row } from '@tanstack/react-table';
import { downloadBlob } from '../lib/util';

type ReceiptBenefits = (typeof receiptBenefits)[number];

class Util {
  public static provinceNames = [
    { text: 'Baden-Württemberg', value: 'Baden-Württemberg' },
    { text: 'Bayern', value: 'Bayern' },
    { text: 'Berlin', value: 'Berlin' },
    { text: 'Brandenburg', value: 'Brandenburg' },
    { text: 'Bremen', value: 'Bremen' },
    { text: 'Hamburg', value: 'Hamburg' },
    { text: 'Hessen', value: 'Hessen' },
    { text: 'Mecklenburg-Vorpommern', value: 'Mecklenburg-Vorpommern' },
    { text: 'Niedersachsen', value: 'Niedersachsen' },
    { text: 'Nordrhein-Westfalen', value: 'Nordrhein-Westfalen' },
    { text: 'Rheinland-Pfalz', value: 'Rheinland-Pfalz' },
    { text: 'Saarland', value: 'Saarland' },
    { text: 'Sachsen', value: 'Sachsen' },
    { text: 'Sachsen-Anhalt', value: 'Sachsen-Anhalt' },
    { text: 'Schleswig-Holstein', value: 'Schleswig-Holstein' },
    { text: 'Thüringen', value: 'Thüringen' }
  ];

  public static getKeys<T extends object>(obj?: T, exclude?: (keyof T)[]): (keyof T)[] {
    return obj
      ? Object.keys(obj)
          .filter((k) => !exclude?.includes(k as keyof T))
          .map((k) => k as keyof T)
      : new Array<keyof T>();
  }

  public static merge<T>(base: T, override: T): T {
    return typeof base === 'object' ? { ...base, ...override } : override;
  }

  public static getFirstDayOfMonth(date?: Date): Date {
    date = date ?? new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  public static getNextDay(date?: Date): Date {
    date = date ?? new Date();
    return new Date(date.setDate(date.getDate() + 1));
  }

  public static getLastDayOfMonth(date?: Date): Date {
    date = date ?? new Date();
    return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
  }

  public static getCurrentMonthString(date?: Date): string {
    date = date ?? new Date();
    const locale = i18n.language === 'en' ? 'en-US' : 'de-DE';
    return new Date(date.getFullYear(), date.getMonth()).toLocaleString(locale, {
      month: 'long',
      year: '2-digit'
    });
  }

  public static getNextMonth(date?: Date, disableFuture = false): Date {
    date = date ?? new Date();
    const nextMonthIsFuture = new Date() < new Date(date.getFullYear(), date.getMonth() + 1, 1);
    if (disableFuture && nextMonthIsFuture) {
      return date;
    }
    return new Date(date.getFullYear(), date.getMonth() + 1, 1);
  }

  public static getPrevMonth(date?: Date): Date {
    date = date ?? new Date();
    return new Date(date.getFullYear(), date.getMonth() - 1, 1);
  }

  public static getDateString(d: Date): string {
    return d.toISOString().split('T')[0];
  }

  public static now = new Date();

  public static getDateBeforeDays(days: number): Date {
    const currentDate = new Date();
    return new Date(currentDate.getTime() - days * 24 * 60 * 60 * 1000);
  }

  public static addDays(days: number, date?: Date): Date {
    date = date ?? new Date();
    return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
  }

  public static addMonth(month: number, date?: Date): Date {
    date = date ?? new Date();
    return new Date(date.getFullYear(), date.getMonth() + month, date.getDay(), 0, 0, 0);
  }

  public static calcNewBudget(data: {
    current?: number;
    increase?: number;
    type?: string;
  }): number {
    const { current, increase, type } = data;

    if (type === 'FIXED_VALUE' && increase) {
      return increase;
    } else if (increase && current) {
      if (type === 'PERCENT_VALUE') {
        return current + Math.round(current * increase) / 100;
      }
    }

    return current || 0;
  }

  public static readonly currencyFilterFn = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    row: Row<any>,
    columnId: string,
    filterValue: [number, number]
  ) => {
    const value = row.getValue(columnId) as number;
    const [min, max] = filterValue ?? [null, null];

    const minInCents = min !== null ? min * 100 : null;
    const maxInCents = max !== null ? max * 100 : null;

    if (minInCents === null && maxInCents === null) return true;
    if (minInCents === null && maxInCents !== null) return value <= maxInCents;
    if (maxInCents === null && minInCents !== null) return value >= minInCents;
    if (minInCents !== null && maxInCents !== null)
      return value >= minInCents && value <= maxInCents;
    return true;
  };

  public static formatCurrency(cents?: number, decimal?: boolean): string {
    const locale = i18n.language === 'en' ? 'en-US' : 'de-DE';
    return (
      ((cents || cents == 0) &&
        (cents / 100.0).toLocaleString(locale, {
          style: 'currency',
          currency: 'EUR',
          maximumFractionDigits: (decimal ?? 1) ? 2 : 0
        })) ||
      ''
    );
  }

  public static formatDate(
    value: Date | string | undefined,
    style: Intl.DateTimeFormatOptions['dateStyle'] = 'medium'
  ): string {
    const d = typeof value === 'string' ? new Date(value) : value;
    const locale = i18n.language === 'en' ? 'en-US' : 'de-DE';

    return d?.toLocaleDateString(locale, { dateStyle: style }) ?? 'invalid date';
  }

  private static parseDateOnly(value: Date | string): Date {
    if (value instanceof Date) return new Date(value);

    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) return new Date(value);

    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  public static formatAge(value: Date | string, t: TFunction): string {
    const start = this.parseDateOnly(value);
    const years = this.getNextYearlyDate(value).getFullYear() - start.getFullYear();

    return years ? `${years} ${t('template:YEARS', { count: years })}` : '';
  }

  static getNextYearlyDate(value: Date | string): Date {
    const d = this.parseDateOnly(value);

    const today = new Date();
    d.setFullYear(today.getFullYear());
    const hasPassedThisYear = d.valueOf() - today.valueOf() < -86400000;

    if (hasPassedThisYear) {
      d.setFullYear(today.getFullYear() + 1);
    }

    return d;
  }

  public static calcNextYearlyDateMilliseconds(value: Date | string): number {
    return this.getNextYearlyDate(value).valueOf();
  }

  public static calcNextYearlyDate(value: Date | string): string {
    return this.formatDate(this.getNextYearlyDate(value));
  }

  public static getMinBirthDate() {
    return this.getMinDate(100);
  }

  public static getMinDate(years: number) {
    const today = new Date();
    return new Date(today.getFullYear() - years, 1, 1);
  }

  public static getMaxDate(years: number) {
    const today = new Date();
    return new Date(today.getFullYear() + years, 1, 1);
  }

  public static parseTwoDigitYearsDate(value: Date | string) {
    // takes any date where the year has 2 digits (provided by the input being 0023-11-27 for 27.11.2023 for instance)
    // and transforms it to a 4 digit value, turning over at 70 years in the past. This means that
    // given the current year is 2023, anything less than 23 and more than 53 is being treated as the past,
    // while anything more than 23 but less than 53 is being treated as the future.
    const turnover = 70;

    const d = typeof value === 'string' ? new Date(value) : value;
    const valueYear = d.getFullYear();

    if (valueYear < 100) {
      const nowYear = new Date().getFullYear();

      const flippingYear = nowYear - turnover;
      const flippingPoint = flippingYear % 100;
      let flippingCentury = Math.floor(flippingYear / 100);

      if (valueYear < flippingPoint) flippingCentury++;

      return new Date(flippingCentury * 100 + valueYear, d.getMonth(), d.getDate());
    }

    return d;
  }

  public static csvToArray = (str: string, delimiter = ';') => {
    const headers = this.getHeadersFromCsv(str, delimiter);
    const rows = str.slice(str.indexOf('\n') + 1).split('\n');

    const arr = rows.map(function (row) {
      const values = row.split(delimiter);
      const el = headers.reduce(function (object: Record<string, string>, header, index) {
        object[header] = values[index].replace(/\s+/g, ''); //remove whitespace
        return object;
      }, {});
      return el;
    });

    return arr;
  };

  public static getHeadersFromCsv = (str: string, delimiter = ';') => {
    return str
      .slice(0, str.indexOf('\n'))
      .split(delimiter)
      .map((header) => header.replace(/\s+/g, ''));
  };

  public static isBenefitActive(
    benefit: Benefit | 'custom',
    benefitSettings: EmployeeBenefitSettings | undefined
  ) {
    if (benefit === 'custom') return true;
    return !!(benefitSettings && benefit in benefitSettings && benefitSettings[benefit].active);
  }

  public static getReceiptsBenefits(employee: EmployeeExtended | EmployeeListItem) {
    let benefits = undefined;

    if ('benefit_settings' in employee) {
      benefits = employee.benefit_settings;
    } else if ('benefits' in employee) {
      benefits = employee.benefits;
    } else return [];

    if (benefits) {
      const list = Object.entries(benefits)
        .filter(([key, value]) => value.active && receiptBenefits?.includes(key as ReceiptBenefits))
        .map(([k]) => k as Benefit);
      return list;
    } else return [];
  }

  private static buildQueryAndFragment(query?: string, fragment?: string) {
    return `${query ? '?' + query : ''}${fragment ? '#' + fragment : ''}`;
  }

  public static popPathDir(uri: string) {
    const uriFragments = uri.split('/');
    const lastFragment = uriFragments.pop();
    const query = lastFragment?.split('?').at(1)?.split('#')?.at(0);
    const fragment = lastFragment?.split('#').at(1);

    return `${uriFragments.join('/')}${this.buildQueryAndFragment(query, fragment)}`;
  }

  public static pushPathDir(uri: string, newDir: string) {
    const uriFragments = uri.split(/[?#]/);
    const path = uriFragments.at(0);
    const query = uriFragments.at(1);
    const fragment = uriFragments.at(2);
    return `${path}/${newDir}${this.buildQueryAndFragment(query, fragment)}`;
  }

  public static getFileExtension = (filename: string) => filename.split('.').reverse().at(0);

  public static downloadFile = async (fileUrl: string, fileName: string) => {
    const response = await fetch(fileUrl);
    downloadBlob(await response.blob(), fileName);
  };

  public static getCurrentMonthField() {
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() + 1 } as MonthField;
  }

  public static isMonthFieldInPast(field: MonthField | null | undefined): boolean {
    if (!field) return false;
    const current = Util.getCurrentMonthField();
    return (
      field.year < current.year || (field.year === current.year && field.month < current.month)
    );
  }

  public static calcBenefitBudget(
    value: number,
    operation: OperationEnum,
    context: { benefit_budget?: number }
  ) {
    switch (operation) {
      case 'FIXED_VALUE':
        return value;
      case 'PERCENT_VALUE':
        return (context.benefit_budget ?? 0) * (1 + value / 100);
    }
  }

  public static assignReadOnly<T>(readOnlyObj: T, key: keyof T, value: T[keyof T]) {
    runInAction(() => {
      (readOnlyObj as Writeable<T>)[key] = value;
    });
  }

  public static getFileSuffix(filename: string) {
    return filename.includes('.') ? filename.split('.').reverse()[0].split('?')[0] : '';
  }

  public static getURLfromBase64FileField(file?: Base64FileField | null) {
    if (file?.url && file?.name) {
      if (file.url.includes('http')) return file.url;
      const mediaType = Mime.getType(this.getFileSuffix(file.name));
      return `data:${mediaType};base64,${file.url}`;
    }
    return '';
  }

  public static getURLfromFileWithPreview(file: FileWithPreview) {
    if (file.file?.toString().includes('http')) return file.file?.toString();
    if (file.preview?.includes('http')) return file.preview;
    const mediaType = file.type ? file.type : Mime.getType(this.getFileSuffix(file.name));
    return `data:${mediaType};base64,${file.file}`;
  }

  public static calcFahrtkosten(km: number) {
    return km < 21 ? km * 0.3 * 15 : 20 * 0.3 + (km - 20) * 0.38;
  }

  public static getAccountingPeriod(date?: Date): MonthFieldRequest {
    date = date ?? new Date();

    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1
    };
  }

  public static getDate(accountingPeriod?: MonthFieldRequest, variant?: 'start' | 'end'): Date {
    if (!accountingPeriod) {
      return new Date();
    }

    return variant === 'start' || variant === undefined
      ? new Date(accountingPeriod.year, accountingPeriod.month - 1)
      : new Date(accountingPeriod.year, accountingPeriod.month, 0);
  }

  public static getValueFromStore(
    value: string | number | undefined | string[],
    type: string | undefined,
    store?: CompanyStore
  ) {
    switch (type) {
      case 'location':
        return store?.locations?.find((location) => location.id === value)?.name;
      case 'department':
        return store?.departments?.find((department) => department.id === value)?.name;
      case 'cost_center':
        return store?.costCenters?.find((costCenter) => costCenter.id === value)?.name;
      case 'groups':
        return store?.groups?.find((groups) => groups.id === value)?.name;
      default:
        return value;
    }
  }

  public static capitalizeFirstLetter(text: string) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  public static lowerCaseFirstLetter(text: string) {
    return text.charAt(0).toLowerCase() + text.slice(1);
  }

  public static getLocale() {
    const locales = {
      de,
      en: enUS
    };
    return locales[i18n.language as keyof typeof locales];
  }

  public static formatDateString(dateString: string, dateFormat = 'yyy-MM-dd') {
    return format(new Date(dateString), dateFormat);
  }

  private static _createIBANErrorMessages = (errorCodes: Array<ValidationErrorsIBAN>) => {
    const messages = errorCodes.map((errorCode) => {
      return t('payment:iban_error_code', { context: errorCode });
    });
    //remove duplicates before returning
    return [...new Set(messages)].join(', ');
  };

  private static _createBICErrorMessages = (errorCodes: Array<ValidationErrorsBIC>) => {
    const messages = errorCodes.map((errorCode) => {
      return t('payment:bic_error_code', { context: errorCode });
    });
    //remove duplicates before returning
    return [...new Set(messages)].join(', ');
  };

  public static formatExcelNumberToDate(num: number) {
    const d = new Date(Date.UTC(0, 0, num - 1, 0, 0, 0));
    return d.toLocaleDateString(i18next.language, { dateStyle: 'medium' });
  }

  public static asUndefined<T>(value: T) {
    return value ? value : undefined;
  }

  public static monthFieldToDate(value: MonthField | undefined) {
    if (!value) return undefined;
    return new Date(value.year, value.month - 1, 1);
  }

  public static formatDateToMonthYear(date: Date) {
    const locale = i18n.language === 'en' ? enUS : de;
    return format(date, 'MMMM yyyy', { locale: locale });
  }

  public static dateToMonthField(date: Date | null | undefined) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return null;
    }

    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1
    };
  }

  public static numberToMonthName(monthValue: number, short: boolean = false) {
    const locale = i18n.language === 'en' ? enUS : de;
    const date = new Date(new Date().getFullYear(), monthValue - 1, 1);
    return format(date, short ? 'MMM' : 'MMMM', { locale });
  }
}

export default Util;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T;
};

// Form Utils

interface HideHandlerParams {
  predicate: (values: FormikValues) => boolean;
  fieldKey?: string;
  initValue?: unknown;
}

export const hideHandler = ({ predicate, fieldKey, initValue }: HideHandlerParams) => {
  return (values: FormikValues) => {
    const isHidden = predicate(values);
    if (!isHidden && fieldKey && initValue && !values[fieldKey]) {
      values[fieldKey] = initValue;
    }
    return isHidden;
  };
};
